/**
 * Social Inbox — Learn-Mode inline tips.
 *
 * Anchored across `routes/social/Inbox.tsx` and the
 * `components/inbox/*` / `ReplyComposer.tsx` / `ConversationThread.tsx`
 * pieces it renders.
 */

import type { LearnTip } from "./types";

export const inboxTips: LearnTip[] = [
  {
    key: "social-inbox",
    title: "Social Inbox",
    body: "Every comment and DM across your connected accounts lands here in one place, so your team can reply without jumping between apps.",
  },
  {
    key: "social-inbox-shortcuts",
    title: "Keyboard Shortcuts",
    body: "Speeds up triage for anyone working the inbox all day — jump between items, reply, or mark done without reaching for the mouse.",
  },
  {
    key: "social-inbox-filters",
    title: "Searching & Filtering",
    body: "Narrow the inbox down to a specific client, platform, or search term. Press “/” anywhere on the page to jump straight to the search box.",
  },
  {
    key: "social-inbox-bulk",
    title: "Bulk Actions",
    body: "Appears once you check two or more items — mark them all done, archive them, or reassign them in one action instead of one at a time.",
  },
  {
    key: "social-inbox-details",
    title: "Item Details",
    body: "The side panel for whichever comment or message you've selected — who it's assigned to, internal notes, and the customer's history with this account.",
  },
  {
    key: "social-inbox-notes",
    title: "Internal Notes",
    body: "Private notes about this conversation that only your team can see — they're never posted publicly, even if the customer replies again.",
  },
  {
    key: "social-inbox-customer-history",
    title: "Customer History",
    body: "A quick look at how often this person has commented before, whether they've been replied to, and if any past comments were flagged negative — useful context before you respond.",
  },
  {
    key: "social-inbox-sync-status",
    title: "Sync Status",
    body: "Shows how recently each connected account's comments and messages were pulled in. A warning icon means a sync is stalled or failed — click to see which account and retry.",
  },
  {
    key: "social-inbox-reply",
    title: "Replying",
    body: "Write your reply here, or use an AI-suggested draft as a starting point — always review an AI suggestion before sending, it's a draft, not a final answer.",
  },
  {
    key: "social-inbox-saved-replies",
    title: "Saved Replies",
    body: "Reusable canned responses for questions you answer often (hours, pricing, booking links). Search by keyword and insert one, then personalize it before sending.",
  },
  {
    key: "social-inbox-triage",
    title: "Smart Triage",
    body: "Comments are sorted automatically. Needs reply holds questions, booking or pricing requests and complaints. Review holds anything the system isn't sure about. No reply needed holds emoji, praise, thanks and friend tags — clear that whole bucket with one click.",
  },
  {
    key: "social-inbox-bucket",
    title: "Move to a Bucket",
    body: "Think a comment is in the wrong bucket? Move it here (or press 1, 2 or 3). Your choice sticks — the automatic sorting never moves it back.",
  },
  {
    key: "social-inbox-response-time",
    title: "Response Time",
    body: "The median time from a comment arriving to its first reply over the last 30 days, plus the share that got a reply. Click it to see each client.",
  },
  {
    key: "social-inbox-moderation",
    title: "Hide on Platform",
    body: "Hides a Facebook or Instagram comment from the public (spam, abuse) without deleting it. The person who wrote it and their friends can still see it. You can unhide it any time.",
  },
  {
    key: "social-inbox-post-context",
    title: "Post This Comment Is On",
    body: "A quick reminder of which post this comment or reply is attached to, so you have the full context before responding.",
  },
  {
    key: "social-inbox-draft-replies",
    title: "Draft Replies",
    body: "Writes a draft reply, in the client's voice, for each comment in Needs reply that doesn't have one yet (up to 25 at a time). Nothing is posted — each draft waits in that comment's reply box for you to check, edit and send.",
    bullets: [
      "Comments that already have a draft are skipped, so clicking again only fills the gaps.",
      'A "Draft ready" tag in the list marks comments with a draft waiting.',
      "Drafts are written by the AI model on our own server — comment text never goes to an outside AI service.",
    ],
  },
  {
    key: "social-inbox-draft-prefill",
    title: "AI Draft in the Reply Box",
    body: "This reply was drafted by AI. Read it, fix anything that isn't right, then send it the usual way. It only goes out when you press Post reply.",
  },
  {
    key: "social-inbox-reviews",
    title: "Reviews",
    body: "Facebook Page reviews and recommendations for your connected pages land here, separate from comments. It isn't a live feed — reviews are harvested on the weekly archive run. There's no public reply path (Meta doesn't offer one for Page reviews) — mark one done once it's been handled, or reopen it if you marked it by mistake.",
  },
  {
    key: "social-inbox-mentions",
    title: "Mentions",
    body: "Posts where another Instagram account tagged you ('this post features you') land here, separate from comments and reviews. It's not a live feed — tags refresh roughly every 15 minutes. There's no reply path (Meta doesn't offer one for tags) — mark one done once you've seen it, or reopen it if you marked it by mistake.",
  },
  {
    key: "social-inbox-dms",
    title: "Direct Messages",
    body: "Every Facebook and Instagram DM across your connected accounts lands here, separate from comments. Meta only allows a reply within 24 hours of the customer's last message — after that, the conversation shows as closed until they message again.",
  },
  {
    key: "social-inbox-dm-composer",
    title: "Replying to a DM",
    body: "Write your reply here. Nothing is sent until you click Send (or press Enter) — there's no auto-send, no scheduled send, and no AI draft that sends on its own.",
  },
  {
    key: "social-inbox-automation",
    title: "Automation",
    body: "Every comment or DM reply the engagement engine has drafted or sent, in one place. A draft waiting for review needs your Approve or Reject — approving posts it immediately, rejecting just discards the draft and leaves the comment open for you to answer normally.",
    bullets: [
      "Turn automation on, and set how cautious it is, per client under Brand Kit → Engagement automation.",
      'Switch the "Show" filter to review recent sent, failed, skipped or rejected activity.',
    ],
  },
  {
    key: "social-inbox-dm-suggestion",
    title: "AI-Drafted DM Reply",
    body: "The engagement engine drafted this reply to the customer's last message. Read it, then Send as reply to post it as-is, or Dismiss to discard it and reply yourself instead — nothing sends without this click.",
  },
  {
    key: "social-inbox-dm-lists",
    title: "Conversations vs. Comment Chats",
    body: "Conversations are real DMs — the person messaged you, and the count shows how many still need a reply. Comment chats are threads Facebook opened automatically when someone commented on a post; that person hasn't messaged you yet, so a reply here is an opportunity rather than a customer waiting on you.",
  },
  {
    key: "social-inbox-dm-linked-comments",
    title: "Also Commented Publicly",
    body: "Public comments this same person left on your posts, so you have the full picture before replying privately. Each one shows whether you've already replied publicly — answering the DM doesn't reply to the comment, so check both.",
  },
  {
    key: "social-inbox-dm-handled",
    title: "Mark Handled Offline",
    body: 'Resolved this outside the inbox — a phone call, an in-person visit, or a reply from the Meta app? Mark it handled so it stops counting as needing a reply. Add an optional note (e.g. "Called her back") so teammates know what happened. Nothing is sent to the customer, and if they message again the conversation shows as needing a reply again.',
  },
];
